import { useState, useEffect } from "react";
import api from "../api/api";
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaTrash,
  FaEdit,
} from "react-icons/fa";

export default function AddUser() {
  const [form, setForm] = useState({
    name: "",
    fullName: "", // ✅ NEW (UI only)
    email: "",
    password: "",
    role: "user",
  });
  const [editingId, setEditingId] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");

  /* ================= FETCH USERS ================= */
  const fetchUsers = async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (err) {
      console.error("Failed to fetch users");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  /* ================= FORM ================= */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingId) {
        // ✅ UPDATE
        await api.put(`/users/${editingId}`, form);
        setMessage("User updated successfully");
      } else {
        // ✅ CREATE
        await api.post("/create-user", form);
        setMessage("User created successfully");
      }

      setForm({
        name: "",
        fullName: "", // ✅ NEW
        email: "",
        password: "",
        role: "user",
      });

      setEditingId(null);
      fetchUsers();
    } catch (err) {
      setMessage(err.response?.data?.message || "Error");
    } finally {
      setLoading(false);
    }
  };
  const handleEdit = (user) => {
    setForm({
      name: user.Name,
      email: user.Email,
      password: "", // don't prefill password
      role: user.Role,
      fullName: user.FullName || "", // ✅ NEW
    });

    setEditingId(user.Id);
    window.scrollTo({ top: 0, behavior: "smooth" }); // nice UX
  };
  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete user?")) return;

    try {
      await api.delete(`/users/${id}`);
      fetchUsers();
    } catch {
      alert("Delete failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto mt-8 space-y-6">
      {/* ================= FORM ================= */}
      <div className="max-w-xl mx-auto mt-8">
        <div className="bg-white border shadow-sm rounded-xl p-6">
          {/* TITLE */}
          <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
            {editingId ? "Edit User" : "Add New User"}
          </h2>

          {/* MESSAGE */}
          {message && (
            <div className="mb-4 text-sm text-center text-green-600">
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <FaUser className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                name="fullName"
                placeholder="Full Name"
                value={form.fullName}
                onChange={handleChange}
                className="w-full border pl-10 pr-3 py-2 rounded-md text-sm focus:ring-2 focus:ring-teal-500 outline-none"
              />
            </div>

            {/* NAME */}
            <div className="relative">
              <FaUser className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                name="name"
                placeholder="User name"
                value={form.name}
                onChange={handleChange}
                className="w-full border pl-10 pr-3 py-2 rounded-md text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                required
              />
            </div>

            {/* EMAIL */}
            <div className="relative">
              <FaEnvelope className="absolute left-3 top-3 text-gray-400" />
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={form.email}
                onChange={handleChange}
                className="w-full border pl-10 pr-3 py-2 rounded-md text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                required
              />
            </div>

            {/* PASSWORD */}
            <div className="relative">
              <FaLock className="absolute left-3 top-3 text-gray-400" />

              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                value={form.password}
                onChange={handleChange}
                className="w-full border pl-10 pr-10 py-2 rounded-md text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                required
              />

              <span
                className="absolute right-3 top-3 cursor-pointer text-gray-400"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            {/* ROLE */}
            <select
              name="role"
              value={form.role}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded-md text-sm focus:ring-2 focus:ring-teal-500 outline-none"
            >
              <option value="user">User</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>

            {/* BUTTON */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 text-white py-2 rounded-md text-sm font-medium hover:bg-teal-700 transition flex justify-center items-center gap-2"
            >
              {loading
                ? editingId
                  ? "Updating..."
                  : "Creating..."
                : editingId
                  ? "Update User"
                  : "Create User"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm({
                    name: "",
                    fullName: "", // ✅ add this
                    email: "",
                    password: "",
                    role: "user",
                  });
                }}
                className="w-full mt-2 bg-gray-200 text-gray-700 py-2 rounded-md text-sm"
              >
                Cancel Edit
              </button>
            )}
          </form>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="bg-white border shadow-sm rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4">Users List</h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-gray-500">
                <th className="text-left py-2">User Name</th>
                <th className="text-left py-2">Email</th>
                <th className="text-left py-2">Role</th>
                <th className="text-center py-2">Actions</th>
              </tr>
            </thead>

            <tbody>
              {users.map((u, i) => (
                <tr
                  key={u.Id}
                  className={`border-b hover:bg-gray-50 ${
                    i % 2 ? "bg-gray-50/40" : ""
                  }`}
                >
                  <td className="py-2">{u.Name}</td>
                  <td className="py-2">{u.Email}</td>
                  <td className="py-2 capitalize">{u.Role}</td>

                  <td className="flex justify-center gap-4 py-2">
                    <FaEdit
                      className="text-blue-500 cursor-pointer"
                      onClick={() => handleEdit(u)}
                    />
                    <FaTrash
                      className="text-red-500 cursor-pointer"
                      onClick={() => handleDelete(u.Id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
